<template>
  <div class="users-profile" style="width:100%;height:100%;text-align: left;">
    <v-toolbar flat>
      <v-toolbar-title class="grey--text" style="margin-right: 8px;min-width: 110px;">设备版本统计</v-toolbar-title>
      <v-text-field
        v-model="searchQuery"
        label="搜索设备名称/ID/医院"
        prepend-icon="search"
        clearable
        hide-details
        single-line
        dense
        class="mx-2"
        style="max-width: 250px;"
        @keyup.enter="loadData(1)"
        @click:clear="loadData(1)"
      ></v-text-field>
      <v-select
        v-model="selectedHospital"
        :items="hospitals"
        label="医院"
        single-line
        hide-details
        class="mx-2"
        style="max-width: 200px;"
        dense
        clearable
        @change="loadData(1)"
      ></v-select>
      <v-select
        v-model="selectedMode"
        :items="deviceModes"
        label="设备型号"
        single-line
        hide-details
        class="mx-2"
        style="max-width: 200px;"
        dense
        clearable
        @change="loadData(1)"
      ></v-select>
      <v-select
        v-model="groupBy"
        :items="groupByOptions"
        label="分组方式"
        single-line
        hide-details
        class="mx-2"
        style="max-width: 150px;"
        dense
        @change="handleGroupByChange"
      ></v-select>
      <v-btn color="primary" small @click="loadData(1)" class="mx-1">查询</v-btn>
      <v-btn small @click="exportData" :loading="exporting" class="mx-1">导出</v-btn>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon @click="loadData(1)">
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>刷新数据</span>
      </v-tooltip>
    </v-toolbar>

    <!-- 统计卡片 -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card outlined height="100%">
          <v-card-text class="text-center">
            <div class="subtitle-1 grey--text">设备总数</div>
            <div class="display-1 font-weight-bold primary--text mt-2">{{ stats.total || 0 }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined height="100%">
          <v-card-text class="text-center">
            <div class="subtitle-1 grey--text">已同步版本设备</div>
            <div class="display-1 font-weight-bold success--text mt-2">{{ stats.syncedCount || 0 }}</div>
            <div class="caption">{{ stats.total > 0 ? Math.round((stats.syncedCount / stats.total) * 100) : 0 }}%</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined height="100%">
          <v-card-text class="text-center">
            <div class="subtitle-1 grey--text">未同步版本设备</div>
            <div class="display-1 font-weight-bold error--text mt-2">{{ stats.unsyncedCount || 0 }}</div>
            <div class="caption">{{ stats.total > 0 ? Math.round((stats.unsyncedCount / stats.total) * 100) : 0 }}%</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 版本分布图表 -->
    <v-card class="mb-4">
      <v-tabs v-model="activeTab" @change="handleTabChange">
        <v-tab>下位机版本分布</v-tab>
        <v-tab>上位机版本分布</v-tab>
        <v-tab>Web前端版本分布</v-tab>
        <v-tab>服务端版本分布</v-tab>
        <v-tab>Node版本分布</v-tab>
        <v-tab>版本组合分析</v-tab>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="chart-container" v-if="hasVersionData('versionDown')">
                <div ref="versionDownChart" class="chart" style="height: 400px;"></div>
              </div>
              <div class="d-flex justify-center align-center" style="height: 400px;" v-else>暂无下位机版本数据</div>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="chart-container" v-if="hasVersionData('versionUp')">
                <div ref="versionUpChart" class="chart" style="height: 400px;"></div>
              </div>
              <div class="d-flex justify-center align-center" style="height: 400px;" v-else>暂无上位机版本数据</div>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="chart-container" v-if="hasVersionData('versionWeb')">
                <div ref="versionWebChart" class="chart" style="height: 400px;"></div>
              </div>
              <div class="d-flex justify-center align-center" style="height: 400px;" v-else>暂无Web前端版本数据</div>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="chart-container" v-if="hasVersionData('versionSrv')">
                <div ref="versionSrvChart" class="chart" style="height: 400px;"></div>
              </div>
              <div class="d-flex justify-center align-center" style="height: 400px;" v-else>暂无服务端版本数据</div>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="chart-container" v-if="hasVersionData('versionNode')">
                <div ref="versionNodeChart" class="chart" style="height: 400px;"></div>
              </div>
              <div class="d-flex justify-center align-center" style="height: 400px;" v-else>暂无Node版本数据</div>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <div class="chart-container" v-if="Object.keys(versionCombinations.up_down).length > 0">
                <div ref="versionCombinationChart" class="chart" style="height: 400px;"></div>
              </div>
              <div class="d-flex justify-center align-center" style="height: 400px;" v-else>暂无版本组合数据</div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-card>

    <!-- 设备列表 -->
    <v-card>
      <v-card-title class="pa-2 pl-4 pr-4">
        <span class="ml-2">设备版本详情</span>
        <v-spacer></v-spacer>
      </v-card-title>
      <v-card-text>
        <!-- 不分组时的设备列表 -->
        <v-data-table
          v-if="groupBy === 'none'"
          :headers="deviceHeaders"
          :items="devices"
          :loading="loading"
          :items-per-page="pageSize"
          :server-items-length="stats.total || 0"
          :footer-props="{
            'items-per-page-options': [10, 20, 50, 100],
            'show-current-page': true,
            'show-first-last-page': true
          }"
          @update:page="handleCurrentChange"
          @update:items-per-page="handleSizeChange"
          class="elevation-1"
        >
          <template v-slot:item.type="{ item }">
            {{ getDeviceTypeName(item.type) }}
          </template>
          <template v-slot:item.mode="{ item }">
            <span v-if="item.mode">{{ item.mode }}</span>
            <span v-else class="grey--text">未知型号</span>
          </template>
          <template v-slot:item.hospitalName="{ item }">
            <span v-if="item.hospitalName">{{ item.hospitalName }}</span>
            <span v-else class="grey--text">未关联医院</span>
          </template>
          <template v-slot:item.versionDown="{ item }">
            <span v-if="item.versionDown">{{ item.versionDown }}</span>
            <span v-else class="grey--text">未同步</span>
          </template>
          <template v-slot:item.versionUp="{ item }">
            <span v-if="item.versionUp">{{ item.versionUp }}</span>
            <span v-else class="grey--text">未同步</span>
          </template>
          <template v-slot:item.versionWeb="{ item }">
            <span v-if="item.versionWeb">{{ item.versionWeb }}</span>
            <span v-else class="grey--text">未同步</span>
          </template>
          <template v-slot:item.versionSrv="{ item }">
            <span v-if="item.versionSrv">{{ item.versionSrv }}</span>
            <span v-else class="grey--text">未同步</span>
          </template>
          <template v-slot:item.versionNode="{ item }">
            <span v-if="item.versionNode">{{ item.versionNode }}</span>
            <span v-else class="grey--text">未同步</span>
          </template>
          <template v-slot:item.updateRealVersion="{ item }">
            <span v-if="item.updateRealVersion">
              {{ formatDate(item.updateRealVersion) }}
            </span>
            <span v-else class="grey--text">未同步</span>
          </template>
        </v-data-table>

        <!-- 按医院或设备型号分组的设备列表 -->
        <div v-else>
          <v-expansion-panels v-if="Object.keys(groupedDevices).length > 0">
            <v-expansion-panel v-for="(group, groupName) in groupedDevices" :key="groupName">
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <span class="font-weight-medium">{{ groupName }}</span>
                  <v-chip class="ml-2" small>{{ group.devices.length }}台设备</v-chip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table
                  :headers="deviceHeaders"
                  :items="group.devices"
                  :items-per-page="-1"
                  hide-default-footer
                  class="elevation-0"
                >
                  <template v-slot:item.type="{ item }">
                    {{ getDeviceTypeName(item.type) }}
                  </template>
                  <template v-slot:item.mode="{ item }">
                    <span v-if="item.mode">{{ item.mode }}</span>
                    <span v-else class="grey--text">未知型号</span>
                  </template>
                  <template v-slot:item.hospitalName="{ item }">
                    <span v-if="item.hospitalName">{{ item.hospitalName }}</span>
                    <span v-else class="grey--text">未关联医院</span>
                  </template>
                  <template v-slot:item.versionDown="{ item }">
                    <span v-if="item.versionDown">{{ item.versionDown }}</span>
                    <span v-else class="grey--text">未同步</span>
                  </template>
                  <template v-slot:item.versionUp="{ item }">
                    <span v-if="item.versionUp">{{ item.versionUp }}</span>
                    <span v-else class="grey--text">未同步</span>
                  </template>
                  <template v-slot:item.versionWeb="{ item }">
                    <span v-if="item.versionWeb">{{ item.versionWeb }}</span>
                    <span v-else class="grey--text">未同步</span>
                  </template>
                  <template v-slot:item.versionSrv="{ item }">
                    <span v-if="item.versionSrv">{{ item.versionSrv }}</span>
                    <span v-else class="grey--text">未同步</span>
                  </template>
                  <template v-slot:item.versionNode="{ item }">
                    <span v-if="item.versionNode">{{ item.versionNode }}</span>
                    <span v-else class="grey--text">未同步</span>
                  </template>
                  <template v-slot:item.updateRealVersion="{ item }">
                    <span v-if="item.updateRealVersion">
                      {{ formatDate(item.updateRealVersion) }}
                    </span>
                    <span v-else class="grey--text">未同步</span>
                  </template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <div v-else class="d-flex justify-center align-center pa-4">
            <v-alert type="info" dense>暂无分组数据</v-alert>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <BcToast ref='toast'></BcToast>
  </div>
</template>

<script>
import axios from 'axios';
import * as echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';
import 'echarts/lib/component/legend';

export default {
  name: 'DeviceVersion',
  data() {
    return {
      loading: false,
      exporting: false,
      searchQuery: '',
      currentPage: 1,
      pageSize: 10,
      selectedMode: null,
      selectedHospital: null,
      deviceModes: [],
      hospitals: [],
      groupBy: 'none',
      groupByOptions: [
        { text: '不分组', value: 'none' },
        { text: '按医院分组', value: 'hospital' },
        { text: '按设备型号分组', value: 'mode' }
      ],
      activeTab: 0,
      stats: {
        total: 0,
        syncedCount: 0,
        unsyncedCount: 0,
        versionDistribution: {
          versionDown: {},
          versionUp: {},
          versionWeb: {},
          versionSrv: {},
          versionNode: {}
        }
      },
      devices: [],
      charts: {},
      // 版本组合统计数据
      versionCombinations: {
        up_down: {}, // 上位机和下位机版本组合
      },
      // 按分组方式组织的设备数据
      groupedDevices: {},
      deviceHeaders: [
        { text: '设备ID', value: 'deviceId', width: '120px' },
        { text: '设备名称', value: 'name', width: '150px' },
        { text: '设备类型', value: 'type', width: '120px' },
        { text: '设备型号', value: 'mode', width: '120px' },
        { text: '所属医院', value: 'hospitalName', width: '180px' },
        { text: '下位机版本', value: 'versionDown', width: '120px' },
        { text: '上位机版本', value: 'versionUp', width: '120px' },
        { text: 'Web前端版本', value: 'versionWeb', width: '120px' },
        { text: '服务端版本', value: 'versionSrv', width: '120px' },
        { text: 'Node版本', value: 'versionNode', width: '120px' },
        { text: '最后同步时间', value: 'updateRealVersion', width: '180px' }
      ]
    };
  },
  mounted() {
    this.loadDeviceModes();
    this.loadHospitals();
    this.loadData(1);
  },
  methods: {
    async loadDeviceModes() {
      try {
        const response = await axios.get('/api/device/mode-list');
        if (response.data && response.data.result) {
          this.deviceModes = response.data.result.map(mode => ({
            text: mode.name || mode.mode || '未知型号',
            value: mode.mode
          }));
        } else {
          console.error('获取设备型号失败:', response.data);
        }
      } catch (error) {
        console.error('加载设备型号失败:', error);
        const errorMsg = error.response && error.response.data ? error.response.data.error : (error.message || '未知错误');
        this.$refs.toast.error('加载设备型号失败: ' + errorMsg);
      }
    },

    async loadHospitals() {
      try {
        const response = await axios.get('/api/hospital/list');
        if (response.data && response.data.result) {
          this.hospitals = response.data.result.map(hospital => ({
            text: hospital.name,
            value: hospital.id
          }));
        } else {
          console.error('获取医院列表失败:', response.data);
        }
      } catch (error) {
        console.error('加载医院列表失败:', error);
        const errorMsg = error.response && error.response.data ? error.response.data.error : (error.message || '未知错误');
        this.$refs.toast.error('加载医院列表失败: ' + errorMsg);
      }
    },

    async loadData(page) {
      this.loading = true;
      try {
        const response = await axios.get('/api/device/version', {
          params: {
            page: page || this.currentPage,
            limit: this.pageSize,
            search: this.searchQuery || undefined,
            mode: this.selectedMode || undefined,
            hospital: this.selectedHospital || undefined,
            groupBy: this.groupBy !== 'none' ? this.groupBy : undefined
          }
        });

        if (response.data.code === 0) {
          this.stats = response.data.data;
          this.devices = response.data.data.devices;
          this.currentPage = response.data.data.page;

          // 处理分组数据
          if (this.groupBy !== 'none' && response.data.data.groupedDevices) {
            this.groupedDevices = response.data.data.groupedDevices;
          } else {
            // 如果后端没有返回分组数据，但前端需要分组显示，则在前端进行分组
            if (this.groupBy !== 'none') {
              this.groupDevices();
            } else {
              this.groupedDevices = {};
            }
          }

          // 分析版本组合
          this.analyzeVersionCombinations();

          // 在下一个渲染周期初始化图表
          this.$nextTick(() => {
            this.initCharts();
            // 如果当前是版本组合分析标签页，初始化版本组合图表
            if (this.activeTab === 5) {
              this.initVersionCombinationChart();
            }
          });
        } else {
          this.$refs.toast.error(response.data.msg || '获取设备版本数据失败');
        }
      } catch (error) {
        console.error('加载设备版本数据失败:', error);
        const errorMsg = error.response && error.response.data ? error.response.data.error : (error.message || '未知错误');
        this.$refs.toast.error('加载设备版本数据失败: ' + errorMsg);
      } finally {
        this.loading = false;
      }
    },

    handleTabChange(tabIndex) {
      // 切换标签页时自动更新数据
      this.loadData(this.currentPage);

      // 当切换到版本组合分析标签页时，初始化版本组合图表
      if (tabIndex === 5 && Object.keys(this.versionCombinations.up_down).length > 0) {
        this.$nextTick(() => {
          this.initVersionCombinationChart();
        });
      }
    },

    handleGroupByChange() {
      // 分组方式变化时重新加载数据
      this.loadData(1);
    },

    // 在前端对设备数据进行分组
    groupDevices() {
      this.groupedDevices = {};

      if (!this.devices || this.devices.length === 0) {
        return;
      }

      // 根据分组方式进行分组
      if (this.groupBy === 'hospital') {
        // 按医院分组
        this.devices.forEach((device) => {
          const groupKey = device.hospitalName || '未关联医院';
          if (!this.groupedDevices[groupKey]) {
            this.groupedDevices[groupKey] = {
              devices: []
            };
          }
          this.groupedDevices[groupKey].devices.push(device);
        });
      } else if (this.groupBy === 'mode') {
        // 按设备型号分组
        this.devices.forEach((device) => {
          const groupKey = device.mode || '未知型号';
          if (!this.groupedDevices[groupKey]) {
            this.groupedDevices[groupKey] = {
              devices: []
            };
          }
          this.groupedDevices[groupKey].devices.push(device);
        });
      }
    },

    analyzeVersionCombinations() {
      // 重置版本组合数据
      this.versionCombinations.up_down = {};

      // 优先使用后端提供的版本组合数据
      if (this.stats.versionCombinations) {
        this.versionCombinations.up_down = this.stats.versionCombinations;
        return;
      }

      // 后端未提供数据，使用前端计算
      // 按设备型号分组
      const devicesByMode = {};
      this.devices.forEach((device) => {
        const mode = device.mode || '未知型号';

        if (!devicesByMode[mode]) {
          devicesByMode[mode] = [];
        }
        devicesByMode[mode].push(device);
      });

      // 分析每个型号的上下位机版本组合
      Object.keys(devicesByMode).forEach((mode) => {
        const devices = devicesByMode[mode];
        const combinations = {};

        devices.forEach((device) => {
          if (device.versionUp && device.versionDown) {
            const combo = `${device.versionUp} + ${device.versionDown}`;
            if (!combinations[combo]) {
              combinations[combo] = {
                count: 0,
                versionUp: device.versionUp,
                versionDown: device.versionDown,
                devices: []
              };
            }
            combinations[combo].count++;
            combinations[combo].devices.push(device);
          }
        });

        // 只保留出现次数最多的组合
        if (Object.keys(combinations).length > 0) {
          const sortedCombos = Object.values(combinations).sort((a, b) => b.count - a.count);
          this.versionCombinations.up_down[mode] = sortedCombos[0];
        }
      });
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.loadData(1);
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.loadData(page);
    },
    hasVersionData(versionType) {
      if (!this.stats.versionDistribution || !this.stats.versionDistribution[versionType]) {
        return false;
      }
      return Object.keys(this.stats.versionDistribution[versionType]).length > 0;
    },
    initCharts() {
      const versionTypes = ['versionDown', 'versionUp', 'versionWeb', 'versionSrv', 'versionNode'];
      const titles = {
        versionDown: '下位机版本分布',
        versionUp: '上位机版本分布',
        versionWeb: 'Web前端版本分布',
        versionSrv: '服务端版本分布',
        versionNode: 'Node版本分布'
      };

      versionTypes.forEach((type) => {
        if (this.hasVersionData(type)) {
          // 如果已有图表实例，先销毁
          if (this.charts[type]) {
            this.charts[type].dispose();
          }

          // 获取DOM元素
          const chartDom = this.$refs[type + 'Chart'];
          if (!chartDom) return;

          // 初始化图表
          this.charts[type] = echarts.init(chartDom);

          // 准备数据
          const distribution = this.stats.versionDistribution[type];

          // 根据类型决定是否按型号分组
          if (type === 'versionDown' || type === 'versionUp') {
            // 使用后端提供的按型号统计的版本分布数据
            const versionByMode = this.stats.versionByMode && this.stats.versionByMode[type];

            if (versionByMode) {
              // 为每个型号创建一个系列
              const series = [];
              const legendData = [];

              Object.keys(versionByMode).forEach((mode, index) => {
                const versionCounts = versionByMode[mode];

                // 转换为图表数据格式
                const data = Object.keys(versionCounts).map(version => ({
                  name: version || '未知版本',
                  value: versionCounts[version]
                }));

                if (data.length > 0) {
                  legendData.push(mode);
                  series.push({
                    name: mode,
                    type: 'pie',
                    radius: [20, 80],
                    center: [(index % 2) * 50 + 25 + '%', Math.floor(index / 2) * 50 + 25 + '%'],
                    roseType: 'area',
                    itemStyle: {
                      borderRadius: 5
                    },
                    label: {
                      show: true,
                      formatter: '{b}: {c}'
                    },
                    data: data
                  });
                }
              });

              // 设置图表选项
              const option = {
                title: {
                  text: titles[type] + '（按型号）',
                  left: 'center'
                },
                tooltip: {
                  trigger: 'item',
                  formatter: '{a} <br/>{b}: {c}'
                },
                legend: {
                  orient: 'vertical',
                  left: 'left',
                  data: legendData
                },
                series: series
              };

              // 渲染图表
              this.charts[type].setOption(option);
            } else {
              // 后端未提供按型号统计数据，使用前端计算
              // 按设备型号分组统计版本
              const devicesByMode = {};
              this.devices.forEach((device) => {
                const mode = device.mode || '未知型号';
                if (!devicesByMode[mode]) {
                  devicesByMode[mode] = [];
                }
                devicesByMode[mode].push(device);
              });

              // 为每个型号创建一个系列
              const series = [];
              const legendData = [];

              Object.keys(devicesByMode).forEach((mode, index) => {
                const devices = devicesByMode[mode];
                const versionCounts = {};

                // 统计该型号下各版本的数量
                devices.forEach((device) => {
                  const version = device[type] || '未知版本';
                  if (!versionCounts[version]) {
                    versionCounts[version] = 0;
                  }
                  versionCounts[version]++;
                });

                // 转换为图表数据格式
                const data = Object.keys(versionCounts).map(version => ({
                  name: version,
                  value: versionCounts[version]
                }));

                if (data.length > 0) {
                  legendData.push(mode);
                  series.push({
                    name: mode,
                    type: 'pie',
                    radius: [20, 80],
                    center: [(index % 2) * 50 + 25 + '%', Math.floor(index / 2) * 50 + 25 + '%'],
                    roseType: 'area',
                    itemStyle: {
                      borderRadius: 5
                    },
                    label: {
                      show: true,
                      formatter: '{b}: {c}'
                    },
                    data: data
                  });
                }
              });

              // 设置图表选项
              const option = {
                title: {
                  text: titles[type] + '（按型号）',
                  left: 'center'
                },
                tooltip: {
                  trigger: 'item',
                  formatter: '{a} <br/>{b}: {c}'
                },
                legend: {
                  orient: 'vertical',
                  left: 'left',
                  data: legendData
                },
                series: series
              };

              // 渲染图表
              this.charts[type].setOption(option);
            }
          } else {
            // Web前端、服务端和Node版本不按型号分组
            const data = Object.keys(distribution).map(version => ({
              name: version || '未知版本',
              value: distribution[version]
            }));

            // 设置图表选项
            const option = {
              title: {
                text: titles[type],
                left: 'center'
              },
              tooltip: {
                trigger: 'item',
                formatter: '{a} <br/>{b}: {c} ({d}%)'
              },
              legend: {
                orient: 'vertical',
                left: 'left',
                data: data.map(item => item.name)
              },
              series: [
                {
                  name: titles[type],
                  type: 'pie',
                  radius: ['40%', '70%'],
                  avoidLabelOverlap: false,
                  label: {
                    show: true,
                    formatter: '{b}: {c} ({d}%)'
                  },
                  emphasis: {
                    label: {
                      show: true,
                      fontSize: '18',
                      fontWeight: 'bold'
                    }
                  },
                  labelLine: {
                    show: true
                  },
                  data: data
                }
              ]
            };

            // 渲染图表
            this.charts[type].setOption(option);
          }
        }
      });

      // 添加窗口大小变化时重绘图表
      window.addEventListener('resize', this.resizeCharts);
    },

    initVersionCombinationChart() {
      // 如果已有图表实例，先销毁
      if (this.charts.versionCombination) {
        this.charts.versionCombination.dispose();
      }

      // 获取DOM元素
      const chartDom = this.$refs.versionCombinationChart;
      if (!chartDom) return;

      // 初始化图表
      this.charts.versionCombination = echarts.init(chartDom);

      // 重新分析版本组合
      this.analyzeVersionCombinations();

      // 准备数据
      const data = [];
      const categories = [];

      // 检查是否有版本组合数据
      if (Object.keys(this.versionCombinations.up_down).length === 0) {
        // 如果没有数据，显示空图表
        const option = {
          title: {
            text: '各型号设备最常见版本组合',
            left: 'center'
          },
          tooltip: {
            trigger: 'item'
          },
          series: []
        };

        // 渲染图表
        this.charts.versionCombination.setOption(option);
        return;
      }

      // 有数据，准备图表数据
      Object.keys(this.versionCombinations.up_down).forEach((mode) => {
        const combo = this.versionCombinations.up_down[mode];
        if (!combo || !combo.versionUp || !combo.versionDown) return;

        categories.push(mode);
        data.push({
          name: mode,
          value: combo.count,
          itemStyle: { color: this.getRandomColor() },
          tooltip: {
            formatter: params => `<div>
                <p><b>${params.name}</b> (${params.value}台设备)</p>
                <p>上位机版本: ${combo.versionUp}</p>
                <p>下位机版本: ${combo.versionDown}</p>
                <p>组合出现次数: ${combo.count}</p>
              </div>`
          }
        });
      });

      // 设置图表选项
      const option = {
        title: {
          text: '各型号设备最常见版本组合',
          left: 'center'
        },
        tooltip: {
          trigger: 'item'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: categories
        },
        series: [
          {
            name: '版本组合',
            type: 'pie',
            radius: ['30%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: true,
              formatter: (params) => {
                const combo = this.versionCombinations.up_down[params.name];
                if (!combo) return params.name;
                return `${params.name}\n${combo.versionUp || '未知'}\n+\n${combo.versionDown || '未知'}`;
              },
              minMargin: 5,
              lineHeight: 15
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '18',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: true
            },
            data: data
          }
        ]
      };

      // 渲染图表
      this.charts.versionCombination.setOption(option);
    },

    getRandomColor() {
      // 生成随机颜色，但避免太浅的颜色
      const letters = '0123456789ABCDEF';
      let color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },
    resizeCharts() {
      Object.values(this.charts).forEach((chart) => {
        if (chart) {
          chart.resize();
        }
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return '未同步';
      try {
        // 尝试解析日期字符串
        const date = new Date(dateStr);
        // 检查日期是否有效
        if (isNaN(date.getTime())) {
          return '日期格式错误';
        }
        // 格式化为 YYYY-MM-DD HH:MM:SS
        return date.getFullYear() + '-'
               + String(date.getMonth() + 1).padStart(2, '0') + '-'
               + String(date.getDate()).padStart(2, '0') + ' '
               + String(date.getHours()).padStart(2, '0') + ':'
               + String(date.getMinutes()).padStart(2, '0') + ':'
               + String(date.getSeconds()).padStart(2, '0');
      } catch (error) {
        console.error('日期格式化错误:', error);
        return '日期格式错误';
      }
    },
    getDeviceTypeName(type) {
      const typeMap = {
        PIVAS: 'PIVAS',
        WARD: '病区',
        PHARMACY: '药房',
        CLINIC: '门诊',
        EMERGENCY: '急诊',
        OTHER: '其他'
      };
      return typeMap[type] || type;
    },
    async exportData() {
      this.exporting = true;
      try {
        // 获取所有数据（不分页）
        const response = await axios.get('/api/device/version', {
          params: {
            limit: 10000,
            page: 1,
            search: this.searchQuery || undefined
          }
        });

        if (response.data.code === 0) {
          const { devices } = response.data.data;
          if (!devices || devices.length === 0) {
            this.$refs.toast.warning('没有数据可导出');
            return;
          }

          // 准备CSV数据
          const headers = [
            '设备ID', '设备名称', '设备类型', '所属医院',
            '下位机版本', '上位机版本', 'Web前端版本', '服务端版本', 'Node版本', '最后同步时间'
          ];

          let csvContent = headers.join(',') + '\n';

          devices.forEach((device) => {
            const row = [
              device.deviceId || '',
              '"' + (device.name || '').replace(/"/g, '""') + '"',
              '"' + (this.getDeviceTypeName(device.type) || '').replace(/"/g, '""') + '"',
              '"' + (device.hospitalName || '').replace(/"/g, '""') + '"',
              device.versionDown || '未同步',
              device.versionUp || '未同步',
              device.versionWeb || '未同步',
              device.versionSrv || '未同步',
              device.versionNode || '未同步',
              device.updateRealVersion ? this.formatDate(device.updateRealVersion) : '未同步'
            ];
            csvContent += row.join(',') + '\n';
          });

          // 创建下载链接
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', `设备版本统计_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$refs.toast.success('导出成功');
        } else {
          this.$refs.toast.error(response.data.msg || '导出数据失败');
        }
      } catch (error) {
        console.error('导出数据失败:', error);
        this.$refs.toast.error('导出数据失败: ' + (error.message || '未知错误'));
      } finally {
        this.exporting = false;
      }
    }
  },
  beforeDestroy() {
    // 移除窗口大小变化监听
    window.removeEventListener('resize', this.resizeCharts);

    // 销毁所有图表实例
    Object.values(this.charts).forEach((chart) => {
      if (chart) {
        chart.dispose();
      }
    });
    this.charts = {};
  }
};
</script>

<style lang="less" scoped>
.users-profile {
  .bc-charts {
    height: 300px;
    width: 100%;
  }
}

/* 响应式调整 */
@media (max-width: 600px) {
  .users-profile {
    .v-card__title {
      flex-direction: column;
      align-items: flex-start;
    }

    .v-spacer {
      display: none;
    }
  }
}
</style>
